<template>
  <CjaButton
    color="gradient-green-blue"
    size="sm"
    @click="saveModal?.openModal()"
    :id="navbarBtnId"
  >
    {{ t("btn.wantToSave") }}
  </CjaButton>

  <Modal ref="saveModal" :maxWidth="{ md: '500px' }">
    <template #header>
      {{ t("modal.title") }}
    </template>
    <template #body>
      <FormSelectionTiles
        :options="navOptions"
        v-model="navOption"
        name="navOption-tiles"
        size="sm"
        @click:option="navigate"
      />
      <hr />
      <div class="newsletter-container">
        <Transition name="fade" mode="out-in">
          <div class="form-container" v-if="status == 'idle'">
            <FormTextInput
              ref="emailInput"
              class="input-email"
              :validation="emailValidation"
              :validation-mode="'none'"
              v-model="email"
              :type="'email'"
              :title="t('input.email.title')"
              :placeholder="t('input.email.placeholder')"
              size="sm"
            />
            <FormCheckboxInput
              ref="tcConsentInput"
              class="input-tc"
              v-model="tcConsent"
              :validation="tcConsentValidation"
              size="sm"
            >
              <i18n-t keypath="input.tcConsent.label.text">
                <NuxtLink
                  external
                  href="/termos-condicoes-gerais"
                  target="_blank"
                >
                  {{ t("input.tcConsent.label.linkTc") }}
                </NuxtLink>
                <NuxtLink external href="/politica-privacidade" target="_blank">
                  {{ t("input.tcConsent.label.linkPp") }}
                </NuxtLink>
              </i18n-t>
            </FormCheckboxInput>
            <CjaButton size="sm" @click="submit" :id="newsletterBtnId">
              {{ t("modal.newsletter.btn") }}
            </CjaButton>
          </div>
          <div class="success-container" v-else-if="status == 'success'">
            <div class="image-container">
              <StoryblokImg
                :image="{
                  filename:
                    'https://a.storyblok.com/f/223704/32x32/35b8e6a6cc/success-icon-light.svg',
                  alt: 'Sucesso',
                }"
              />
            </div>
            <div class="text-container">
              <p class="title">
                {{ t("modal.newsletter.success.title") }}
              </p>
              <p>
                {{ t("modal.newsletter.success.description") }}
              </p>
            </div>
          </div>
          <LoadingSpinner :size="60" :border="6" v-else />
        </Transition>
      </div>
      <hr />
      <div class="call-container">
        <div class="title">
          {{ t("modal.call.title") }}
        </div>
        <div class="call-wrapper">
          <div class="call-link">
            <span class="m-cgg-icon--phone-fill" />
            <a href="tel:211165765">{{ t("contact.phone") }}</a>
          </div>
          <div class="call-schedule">
            {{ t("contact.schedule") }}
          </div>
        </div>
        <div class="call-cost">
          {{ t("contact.cost") }}
        </div>
      </div>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
defineProps<{
  navbarBtnId?: string;
  newsletterBtnId?: string;
}>();

const { t } = useI18n();

const saveModal = ref();

const navOption = ref();
const navOptions = [
  {
    value: "hl",
    label: t("navOption.hl"),
    image:
      "https://a.storyblok.com/f/223704/48x48/2f88477605/vertical-hl-finalidade-main-style-default.svg",
  },
  {
    value: "pl",
    label: t("navOption.pl"),
    image:
      "https://a.storyblok.com/f/223704/48x48/f934933921/vertical-pl-finalidade-main-style-default.svg",
  },
  {
    value: "en",
    label: t("navOption.en"),
    image:
      "https://a.storyblok.com/f/223704/48x48/cfbef6db0a/vertical-en-finalidade-main-style-default.svg",
  },
  {
    value: "bb",
    label: t("navOption.bb"),
    image:
      "https://a.storyblok.com/f/223704/48x48/f8c6a1a49b/vertical-bb-finalidade-main-style-default.svg",
  },
];

const navMap = {
  hl: "/credito-habitacao/simular",
  pl: "/credito-pessoal/simular",
  en: "/energia/simular",
  bb: "/tv-net-voz/simular",
};

const navigate = async () => {
  toggleScroll();

  await navigateTo(
    {
      path: navMap[navOption.value],
    },
    {
      external: true,
    }
  );
};

const {
  submit,
  status,
  email,
  emailInput,
  emailValidation,
  tcConsent,
  tcConsentInput,
  tcConsentValidation,
} = useNewsletterSignup();
</script>

<style lang="scss" scoped>
hr {
  height: 1px;
  background: $light-grey;
  margin: 16px 0;
  border: none;
}

.newsletter-container {
  .form-container {
    display: grid;
    gap: 16px;
    align-items: flex-end;

    @include screen-sm-min {
      gap: 8px 16px;
      grid-template-columns: 1fr auto;
    }

    .input-tc {
      @include screen-sm-min {
        grid-column: 1 / 3;
        grid-row: 2 / 3;
      }
    }
  }

  .spinner {
    margin: 0 auto;
  }

  .success-container {
    .image-container {
      display: flex;
      justify-content: center;
      margin-bottom: 16px;

      img {
        display: block;
        width: 32px;
        height: 32px;
      }
    }

    .text-container {
      text-align: center;

      .title {
        font-weight: 700;
        margin: 0 0 16px;
      }

      p:not(.title) {
        white-space-collapse: preserve;
      }
    }
  }
}

.call-container {
  .title {
    font-size: 14px;
    font-weight: 700;
    margin-bottom: 8px;
  }

  .call-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 13px;
    margin-bottom: 8px;

    .call-link {
      display: flex;
      align-items: center;
      gap: 8px;

      .m-cgg-icon--phone-fill {
        color: $main-blue;
        font-size: 16px;
      }

      a {
        color: inherit;
        text-decoration-line: underline;
        cursor: pointer;
        transition: 0.2s ease-in-out;

        &:hover {
          color: $main-blue;
          text-decoration: none;
        }
      }
    }

    .call-schedule {
      border-left: 1px solid $light-grey;
      padding-left: 12px;
    }
  }

  .call-cost {
    color: $dark-grey;
    font-size: 13px;
  }
}

@include fade-transition;
</style>

<i18n lang="json">
{
  "pt": {
    "btn": {
      "wantToSave": "Quero poupar"
    },
    "navOption": {
      "hl": "Crédito Habitação",
      "pl": "Crédito Pessoal",
      "en": "Energia",
      "bb": "Telecomunicações"
    },
    "modal": {
      "title": "Quero poupar em:",
      "tiles": {
        "hl": "Crédito Habitação",
        "pl": "Crédito Pessoal",
        "en": "Energia",
        "bb": "Telecomunicações"
      },
      "newsletter": {
        "btn": "Receber newsletter",
        "success": {
          "title": "Obrigado por subscreveres a newsletter",
          "description": "Receberás em breve as melhores dicas de poupança e acesso a ofertas exclusivas.\nBem-vindo à nossa comunidade!"
        }
      },
      "call": {
        "title": "Quero ligar para me ajudarem"
      }
    },
    "input": {
      "email": {
        "title": "Aprende a poupar connosco!"
      }
    }
  }
}
</i18n>
